import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Offcanvas, Nav } from 'react-bootstrap';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import styles from './Sidebar.module.css';
import { IconType } from 'react-icons';
import { FiGrid, FiBriefcase, FiUsers, FiUser, FiFileText, FiCheckSquare, FiLayers, FiShield, FiBarChart2, FiChevronDown, FiChevronUp, FiChevronsLeft, FiChevronsRight, FiClipboard, FiGitBranch, FiSettings, FiExternalLink } from 'react-icons/fi';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { useT } from '../../i18n';
import { BACKOFFICE_URL, podeAcessarBackoffice, usuarioLogadoEmail } from '../../utils/backoffice';
// Importação da imagem como string (URL)
import logo from '../../assets/FourHand-logo.png';

interface MenuItem {
    to?: string;
    text: string;
    icon: IconType;
    // Identificador estável do grupo (independe do idioma) — usado para saber qual
    // submenu está aberto sem quebrar ao trocar de idioma com um grupo expandido.
    groupKey?: string;
    subItems?: Array<{ to: string; text:string }>;
    // Link pra fora do app (outra aplicação, outra origem) — abre em nova aba via <a>
    // normal, em vez do NavLink do react-router (que só sabe navegar dentro do app).
    external?: boolean;
}

const buildMenuItems = (t: ReturnType<typeof useT>, usuarioEmail?: string): MenuItem[] => [
    { to: "/app/dashboard", text: t('sidebar.dashboard'), icon: FiGrid },
    {
        text: t('sidebar.empresas.title'),
        groupKey: 'empresas',
        icon: FiBriefcase,
        subItems: [
            { to: "/app/empresas", text: t('sidebar.action.cadastrarNova') },
            { to: "/app/empresas/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    {
        text: t('sidebar.estruturaOrganizacional.title'),
        groupKey: 'estruturaOrganizacional',
        icon: FiGitBranch,
        subItems: [
            { to: "/app/estrutura-organizacional", text: t('sidebar.action.cadastrarNova') },
            { to: "/app/estrutura-organizacional/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    {
        text: t('sidebar.fornecedores.title'),
        groupKey: 'fornecedores',
        icon: FiClipboard,
        subItems: [
            { to: "/app/contratos", text: t('sidebar.action.cadastrarNovo') },
            { to: "/app/contratos/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    {
        text: t('sidebar.usuarios.title'),
        groupKey: 'usuarios',
        icon: FiUsers,
        subItems: [
            { to: "/app/usuarios", text: t('sidebar.action.cadastrarNovo') },
            { to: "/app/usuarios/list", text: t('sidebar.action.listarEditar') },
            { to: "/app/perfis-acesso", text: t('sidebar.usuarios.perfisAcesso') },
        ]
    },
    {
        text: t('sidebar.pessoas.title'),
        groupKey: 'pessoas',
        icon: FiUser,
        subItems: [
            { to: "/app/pessoas", text: t('sidebar.action.cadastrarNova') },
            { to: "/app/pessoas/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    {
        text: t('sidebar.perguntas.title'),
        groupKey: 'perguntas',
        icon: FiFileText,
        subItems: [
            { to: "/app/perguntas", text: t('sidebar.action.cadastrarNova') },
            { to: "/app/perguntas/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    {
        text: t('sidebar.questionarios.title'),
        groupKey: 'questionarios',
        icon: FiCheckSquare,
        subItems: [
            { to: "/app/questionarios/novo", text: t('sidebar.action.cadastrarNovo') },
            { to: "/app/questionarios/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    {
        text: t('sidebar.frameworks.title'),
        groupKey: 'frameworks',
        icon: FiLayers,
        subItems: [
            { to: "/app/frameworks", text: t('sidebar.action.cadastrarNovo') },
            { to: "/app/frameworks/list", text: t('sidebar.action.listarEditar') },
        ]
    },
    { to: "/app/dominios", text: t('sidebar.dominiosControle'), icon: FiShield },
    { to: "/app/relatorios", text: t('sidebar.relatorios'), icon: FiBarChart2 },
    {
        text: t('sidebar.cadastrosAuxiliares.title'),
        groupKey: 'cadastrosAuxiliares',
        icon: FiSettings,
        subItems: [
            { to: "/app/cadastros-auxiliares/tipo-empresa", text: t('sidebar.cadastrosAuxiliares.tipoEmpresa') },
            { to: "/app/cadastros-auxiliares/ramo-atuacao", text: t('sidebar.cadastrosAuxiliares.ramoAtuacao') },
            { to: "/app/cadastros-auxiliares/regulamentacao-setorial", text: t('sidebar.cadastrosAuxiliares.regulamentacaoSetorial') },
            { to: "/app/cadastros-auxiliares/tipo-endereco", text: t('sidebar.cadastrosAuxiliares.tipoEndereco') },
            { to: "/app/cadastros-auxiliares/diretorias", text: t('sidebar.cadastrosAuxiliares.diretorias') },
            { to: "/app/cadastros-auxiliares/gerencias", text: t('sidebar.cadastrosAuxiliares.gerencias') },
            { to: "/app/cadastros-auxiliares/departamentos", text: t('sidebar.cadastrosAuxiliares.departamentos') },
            { to: "/app/cadastros-auxiliares/tipo-pessoa", text: t('sidebar.cadastrosAuxiliares.tipoPessoa') },
            { to: "/app/cadastros-auxiliares/tipo-usuario", text: t('sidebar.cadastrosAuxiliares.tipoUsuario') },
            { to: "/app/cadastros-auxiliares/escopo-questionario", text: t('sidebar.cadastrosAuxiliares.escopoQuestionario') },
            { to: "/app/cadastros-auxiliares/classificacao-conformidade", text: t('sidebar.cadastrosAuxiliares.classificacaoConformidade') },
        ]
    },
    ...(podeAcessarBackoffice(usuarioEmail)
        ? [{ to: BACKOFFICE_URL, text: 'BackOffice', icon: FiExternalLink, external: true }]
        : []),
];

const Sidebar: React.FC = () => {
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const { isSidebarCollapsed, toggleSidebar } = useInterfaceStore();
    const isDesktop = useMediaQuery('(min-width: 992px)');
    const t = useT();
    const usuarioEmail = usuarioLogadoEmail();
    const menuItems = buildMenuItems(t, usuarioEmail);

    const IconChevronUp = FiChevronUp as any;
    const IconChevronDown = FiChevronDown as any;
    const IconChevronsLeft = FiChevronsLeft as any;
    const IconChevronsRight = FiChevronsRight as any;

    const handleGroupClick = (groupText: string) => {
        if (isSidebarCollapsed) {
            toggleSidebar(); // Expande a sidebar se estiver recolhida ao clicar em um grupo
        }
        setOpenGroup(openGroup === groupText ? null : groupText);
    };

    return (
        <>
            {/* Sidebar para telas grandes */}
            <aside className={`${styles.sidebar} ${isSidebarCollapsed ? styles.collapsed : styles.open} ${styles.sidebarDesktop}`}>
                <div className={styles.header}>
                    {isSidebarCollapsed ? <img src={logo} alt="4C" className={styles.logoCollapsed} /> : <img src={logo} alt="4Comply Logo" className={styles.logo} />}
                </div>
                <Nav as="ul" className={styles.nav}>
                    {menuItems.map((item, index) => {
                        const Icon = item.icon as any;
                        return item.subItems ? (
                            <Nav.Item as="li" key={index} className={styles.navGroup}>
                                <div className={styles.navGroupTitle} onClick={() => handleGroupClick(item.groupKey!)}>
                                    <div className={styles.navGroupLabel}>
                                        <Icon />
                                        <span>{item.text}</span>
                                    </div>
                                    <div className={styles.groupArrow}>
                                        {openGroup === item.groupKey ? <IconChevronUp /> : <IconChevronDown />}
                                    </div>
                                </div>
                                <ul className={`${styles.navSubMenu} ${openGroup === item.groupKey ? styles.open : ''}`}>
                                    {item.subItems.map((subItem, subIndex) => (
                                        <li key={subIndex} className={styles.navSubItem}>
                                            <NavLink to={subItem.to} className={({ isActive }) => (isActive ? styles.active : '')}>
                                                {subItem.text}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </Nav.Item>
                        ) : item.external ? (
                            <Nav.Item as="li" key={index} className={styles.navItem}>
                                <a href={item.to} target="_blank" rel="noopener noreferrer" title={item.text}>
                                    <Icon />
                                    <span>{item.text}</span>
                                </a>
                            </Nav.Item>
                        ) : (
                            <Nav.Item as="li" key={index} className={styles.navItem}>
                                <NavLink to={item.to!} className={({ isActive }) => (isActive ? styles.active : '')} title={item.text}>
                                    <Icon />
                                    <span>{item.text}</span>
                                </NavLink>
                            </Nav.Item>
                        );
                    })}
                </Nav>
                <div className={styles.footer} onClick={toggleSidebar}>
                    {isSidebarCollapsed ? <IconChevronsRight /> : <IconChevronsLeft />}
                </div>
            </aside>

            {/* Offcanvas para telas pequenas */}
            <Offcanvas show={!isSidebarCollapsed && !isDesktop} onHide={toggleSidebar} className={styles.offcanvasMobile}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title><img src={logo} alt="4Comply Logo" className={styles.logo} /></Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Nav as="ul" className={styles.nav}>
                        {menuItems.map((item, index) => {
                            const Icon = item.icon as any;
                            return item.subItems ? (
                                <Nav.Item as="li" key={index} className={styles.navGroup}>
                                    <div className={styles.navGroupTitle} onClick={() => handleGroupClick(item.groupKey!)}>
                                        <div className={styles.navGroupLabel}>
                                            <Icon />
                                            <span>{item.text}</span>
                                        </div>
                                        <div className={styles.groupArrow}>
                                            {openGroup === item.groupKey ? <IconChevronUp /> : <IconChevronDown />}
                                        </div>
                                    </div>
                                    <ul className={`${styles.navSubMenu} ${openGroup === item.groupKey ? styles.open : ''}`}>
                                        {item.subItems.map((subItem, subIndex) => (
                                            <li key={subIndex} className={styles.navSubItem}>
                                                <NavLink to={subItem.to} className={({ isActive }) => (isActive ? styles.active : '')} onClick={toggleSidebar}>
                                                    {subItem.text}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </Nav.Item>
                            ) : item.external ? (
                                <Nav.Item as="li" key={index} className={styles.navItem}>
                                    <a href={item.to} target="_blank" rel="noopener noreferrer" title={item.text}>
                                        <Icon />
                                        <span>{item.text}</span>
                                    </a>
                                </Nav.Item>
                            ) : (
                                <Nav.Item as="li" key={index} className={styles.navItem}>
                                    <NavLink to={item.to!} className={({ isActive }) => (isActive ? styles.active : '')} title={item.text} onClick={toggleSidebar}>
                                        <Icon />
                                        <span>{item.text}</span>
                                    </NavLink>
                                </Nav.Item>
                            );
                        })}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default Sidebar;